import Navbar from "../../components/Layout/Navbar";
import Footer from "../../components/Layout/Footer";
import PageMeta from "../../components/pagesHelmet/PageMeta";
import URL_IMG from "./image/u2lr.jpeg";

const BRAND = {
  primary: "#3B82F6",
  primaryDark: "#2563EB",
  primarySoft: "rgba(59, 130, 246, 0.08)",
  muted: "#6B7280",
  text: "#1F2937",
};

export default function UrlShortenerPage() {
  return (
    <>
      <PageMeta
        title="Branded URL Shortener | U2LR"
        description="Create custom short links, branded domains, and track clicks with U2LR's powerful URL shortener."
      />
      <Navbar />

      <div style={{ color: BRAND.text }}>

        {/* ================= HERO ================= */}
        <section
          style={{
            background: `linear-gradient(135deg, #0f172a, ${BRAND.primaryDark})`,
            color: "#fff",
          }}
        >
          <div className="container py-5">
            <div className="row py-5 align-items-center">
              <div className="col-lg-6">
                <h1 className="display-4 fw-bold mb-4">
                  Shorten Links. <br /> Build Trust. Track Performance.
                </h1>

                <p className="lead mb-4" style={{ opacity: 0.9 }}>
                  Create branded short URLs with real-time analytics,
                  enterprise-grade reliability, and full control over every click.
                </p>

                <div className="d-flex gap-3 flex-wrap">
                  <a
                    href="/register"
                    className="btn btn-light btn-lg rounded-pill px-4 fw-semibold"
                    style={{ color: BRAND.primaryDark }}
                  >
                    Get Started Free
                  </a>

                  <a
                    href="/pricing"
                    className="btn btn-outline-light btn-lg rounded-pill px-4"
                  >
                    View Pricing
                  </a>
                </div>

                <p className="mt-3 small" style={{ opacity: 0.7 }}>
                  No credit card required • 99.9% uptime • Secure HTTPS
                </p>
              </div>

              <div className="col-lg-6 text-center">
                <img
                  src={URL_IMG}
                  alt="U2LR Dashboard"
                  className="img-fluid rounded-4 shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ================= SOCIAL PROOF STRIP ================= */}
        <section className="py-4 bg-light text-center">
          <div className="container">
            <div className="row text-muted small fw-semibold">
              <div className="col-md-3">✔ High-Speed Global Redirects</div>
              <div className="col-md-3">✔ Real-Time Click Analytics</div>
              <div className="col-md-3">✔ Custom Branded Links</div>
              <div className="col-md-3">✔ Enterprise-Grade Security</div>
            </div>
          </div>
        </section>

        {/* ================= FEATURES ================= */}
        <section className="py-5" style={{ background: BRAND.primarySoft }}>
          <div className="container py-4">

            <div className="text-center mb-5">
              <h2 className="fw-bold">Everything You Need to Scale Links</h2>
              <p style={{ color: BRAND.muted }}>
                Built for performance, analytics, and brand growth.
              </p>
            </div>

            <div className="row g-4">
              {[
                {
                  title: "Custom Branded Links",
                  desc: "Create memorable short URLs with custom aliases and branded domains.",
                },
                {
                  title: "Advanced Analytics",
                  desc: "Track clicks, devices, browsers, locations, and referrers in real time.",
                },
                {
                  title: "Link Management",
                  desc: "Edit, expire, organize, and control all links from one dashboard.",
                },
                {
                  title: "Campaign Optimization",
                  desc: "Identify high-performing channels and improve ROI.",
                },
                {
                  title: "Secure Infrastructure",
                  desc: "HTTPS encryption and abuse protection built in.",
                },
                {
                  title: "Scalable Architecture",
                  desc: "Handle thousands or millions of redirects seamlessly.",
                },
              ].map((item, i) => (
                <div key={i} className="col-md-6 col-lg-4">
                  <div className="card h-100 border-0 shadow-sm rounded-4 p-4">
                    <h5 className="fw-bold mb-3">{item.title}</h5>
                    <p className="mb-0" style={{ color: BRAND.muted }}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= USE CASES ================= */}
        <section className="py-5">
          <div className="container text-center">
            <h2 className="fw-bold mb-5">Built for Modern Teams</h2>

            <div className="row g-4">
              {[
                "Marketing Campaigns",
                "Social Media & Influencers",
                "E-commerce Stores",
                "Agencies & Enterprises",
              ].map((item, i) => (
                <div key={i} className="col-md-3">
                  <div className="shadow-sm rounded-4 p-4 h-100 bg-white">
                    <h6 className="fw-semibold mb-0">{item}</h6>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= HOW IT WORKS ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold mb-5">How It Works</h2>

            <div className="row g-4">
              {[
                "Paste your long URL",
                "Customize your short link",
                "Share anywhere",
                "Track performance instantly",
              ].map((step, i) => (
                <div key={i} className="col-md-3">
                  <div className="p-4">
                    <div
                      className="fw-bold mb-3"
                      style={{
                        fontSize: "2rem",
                        color: BRAND.primary,
                      }}
                    >
                      {i + 1}
                    </div>
                    <p className="mb-0">{step}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= VALUE STRIP ================= */}
        <section className="py-5 text-center">
          <div className="container">
            <div className="row g-4">
              <div className="col-md-4">
                <h3 className="fw-bold">99.9%</h3>
                <p style={{ color: BRAND.muted }}>Uptime Guarantee</p>
              </div>
              <div className="col-md-4">
                <h3 className="fw-bold">Real-Time</h3>
                <p style={{ color: BRAND.muted }}>Click & Device Tracking</p>
              </div>
              <div className="col-md-4">
                <h3 className="fw-bold">Secure</h3>
                <p style={{ color: BRAND.muted }}>HTTPS & Abuse Protection</p>
              </div>
            </div>
          </div>
        </section>

        {/* ================= FINAL CTA ================= */}
        <section
          className="py-5 text-center"
          style={{
            background: BRAND.primaryDark,
            color: "#fff",
          }}
        >
          <div className="container py-4">
            <h2 className="fw-bold mb-3">
              Start Shortening Smarter Today
            </h2>
            <p className="mb-4" style={{ opacity: 0.85 }}>
              Join U2LR and turn every link into measurable growth.
            </p>
            <a
              href="/register"
              className="btn btn-light btn-lg rounded-pill px-5 fw-semibold"
              style={{ color: BRAND.primaryDark }}
            >
              Create Free Account
            </a>
          </div>
        </section>

      </div>

      <Footer />
    </>
  );
}
